/** @format */

import {Box} from '@mui/material';
import {clsx} from 'clsx';
import {Trans} from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';
import LegendToggleIcon from '@mui/icons-material/LegendToggle';
import SensorsIcon from '@mui/icons-material/Sensors';

import {useNavigate, useSearchParams} from 'react-router-dom';

import {SidebarStep} from 'types';

import {StyledBox, classes} from '../styles';

type ExtendedSidebarStep = SidebarStep;

interface TransformedStep {
  name: string;
  path: string;
  key: 'CREATE' | 'MEDIAPLAN' | 'TYPE' | 'PERIODS' | 'SENSORS' | 'FEEDS' | 'FUNNEL';
  isVisible: boolean;
  icon?: React.ReactNode;
}

const STEPS: ExtendedSidebarStep[] = [
  {
    isVisible: true,
    name: 'Создание проекта',
    key: 'CREATE',
    icon: <AddIcon className={classes.stepButtonIcon} />,
  },
  {
    isVisible: false,
    name: 'Медиаплан',
    key: 'MEDIAPLAN',
    icon: (
      <LegendToggleIcon className={clsx(classes.stepButtonIcon, classes.stepButtonIconHeading)} />
    ),
  },
  {
    isVisible: false,
    name: 'Тип кампаний',
    key: 'TYPE',
    icon: <CallSplitIcon className={clsx(classes.stepButtonIcon, classes.stepButtonIconRotated)} />,
  },
  {
    isVisible: false,
    name: 'Период, бюджет и KPI',
    key: 'PERIODS',
    icon: <CurrencyRubleIcon className={classes.stepButtonIcon} />,
  },
  {
    isVisible: false,
    name: 'Сенсоры',
    key: 'SENSORS',
    icon: <SensorsIcon className={classes.stepButtonIcon} />,
  },
  {
    isVisible: true,
    name: 'Фиды',
    key: 'FEEDS',
  },
  {
    isVisible: true,
    name: 'Воронка',
    key: 'FUNNEL',
  },
];

type StepStatus = {
  stepKey: string;
  status: 'done' | 'active';
};

interface Props {
  activeStep: string;
  stepStatus?: StepStatus;
  // temporary flag for storybook
  enableAll?: boolean;
  projectName?: string;
}

const getPath = (key: string, companyId: string | null) => {
  const lowerCaseKey = key.toLowerCase();

  if (!companyId) return '';

  if (key === 'CREATE') {
    return `/home/project/wizard/created?companyId=${companyId}`;
  }

  return `/home/project/${lowerCaseKey}?companyId=${companyId}`;
};

const SidebarEdit = ({activeStep, enableAll, stepStatus, projectName}: Props) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get('companyId');

  const transformNames = (step: ExtendedSidebarStep): TransformedStep => {
    return {
      ...step,
      path: getPath(step.key, companyId),
    };
  };

  const isStepVisible = (step: TransformedStep) => enableAll || step.isVisible;

  const handleStepClick = (path: string) => {
    const clickedStep = steps.find(step => step.path === path);
    if (!clickedStep) return;

    const currentStepIndex = steps.findIndex(step => step.key === activeStep);
    const clickedStepIndex = steps.findIndex(step => step.key === clickedStep.key);

    if (clickedStepIndex > currentStepIndex) {
      return;
    }

    navigate(path);
  };

  let steps = [...STEPS].map(transformNames);

  steps = steps.filter(isStepVisible);

  const getCheckIcon = (stepKey: ExtendedSidebarStep['key']) => {
    const currentStepIndex = steps.findIndex(step => step.key === activeStep);
    const stepIndex = steps.findIndex(step => step.key === stepKey);

    if ((activeStep === 'CREATE' && stepKey === 'CREATE') || stepKey === 'MEDIAPLAN') {
      return null;
    }

    if (stepStatus && stepStatus.stepKey === stepKey && stepStatus.status === 'done') {
      return <CheckCircleIcon className={classes.statusDoneIcon} />;
    }

    if (stepIndex < currentStepIndex) {
      return <CheckCircleIcon className={classes.statusDoneIcon} />;
    }

    return null;
  };

  return (
    <StyledBox>
      <Box className={classes.sidebar}>
        {steps.map(step => (
          <Box
            key={step.key}
            className={clsx(
              classes.stepButton,
              step.key === 'CREATE' && steps.length === 1 && classes.stepButtonCreate,
              step.key === 'MEDIAPLAN' && classes.stepButtonHeading,
              activeStep === step.key && classes.stepButtonActive,
            )}
            sx={{cursor: 'pointer'}}
            onClick={() => handleStepClick(step.path)}
          >
            {step.key === 'CREATE' && projectName?.length ? null : step.icon}

            {projectName || <Trans>{step.name}</Trans>}

            {getCheckIcon(step.key)}
          </Box>
        ))}
      </Box>
    </StyledBox>
  );
};

export default SidebarEdit;
